<template>
    <page :busy="busy" :tabs="tabs">
        <grid
            :columnDefs="columnDefs"
            :rowData="rowData"
            ref="grid"
            class="metadata-grid"
            :isAutoHeight="true"
            rowSelection="single"
            @selection-changed="handleRowSelect"
            :isAutoWidth="true"
        >
            <responsive-button @click="$bvModal.show(createMetaDataModalId)" :icon="['fas', 'plus']" variant="primary"
                >Add Amenity Metadata
            </responsive-button>
        </grid>
        <grid
            v-if="selectedAmenityRow"
            :columnDefs="propertiesColumnDefs"
            :rowData="propertiesList"
            ref="property_grid"
            :isAutoHeight="true"
            :isAutoWidth="true"
            class="pt-2 metadata-property-grid"
        >
            <responsive-dropdown :items="propertiesFilterOptions" variant="default">
                <span v-html="filterIcon" />{{ filterTitle }}
            </responsive-dropdown>
            <responsive-button @click="bulkAssignMetaDataToProperty" variant="default" :icon="['fas', 'plus']"
                >Add "{{ selectedRowName }}" to All Properties</responsive-button
            >
            <responsive-button @click="bulkRemoveMetaDataFromProperty" variant="default" :icon="['fas', 'minus']"
                >Remove "{{ selectedRowName }}" from All Properties</responsive-button
            >
        </grid>
        <create-edit-amenity-meta-data-modal
            ref="create_amenity_metadata"
            @submit="createAmenity"
            :modalId="createMetaDataModalId"
        />
    </page>
</template>

<script>
    import API from '../../../api.jsx';
    import isEmpty from 'lodash/isEmpty';
    import { AMENITIES_META_DATA_LIST_ENDPOINT } from '../../../endpoints';
    import Grid from '../../generic/grid/grid.vue';
    import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';
    import FormSelect from '../../generic/form-select/form-select.vue';
    import page from '../../generic/page/page.vue';
    import Modal from '../../generic/modal/modal.vue';
    import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
    import {
        getAmenityAdditionalMetaDataOptions,
        generateRequestBody,
        parseMetaDataToHumanReadString,
    } from './helpers.js';
    import SearchMultiselect from '../../generic/search-multiselect/search-multiselect.vue';
    import ActionsCellRenderer from './actions-cell-renderer.vue';
    import FormInput from '../../generic/form-input/form-input.vue';
    import CreateEditAmenityMetaDataModal from './create-edit-amenity-meta-data-modal.vue';
    import PropertiesActionsCellRenderer from './properties-actions-cell-renderer.vue';
    import { formatResponseError } from '../../../utils/api.js';
    import AssignIcon from './assign_icon.svg';
    import UnAssignIcon from './unassign_icon.svg';

    export default {
        components: {
            page,
            Grid,
            ResponsiveButton,
            Modal,
            FormSelect,
            SearchMultiselect,
            FormInput,
            CreateEditAmenityMetaDataModal,
            ResponsiveDropdown,
            actionsCellRenderer: ActionsCellRenderer,
            propertiesActionsCellRenderer: PropertiesActionsCellRenderer,
        },
        data() {
            return {
                assignedPropertiesFilter: true,
                busy: null,
                desc: false,
                filter: '',
                propertiesList: [],
                rowData: [],
                selectedAmenityRow: null,
                tabs: [
                    {
                        id: 'policies',
                        title: 'Policies',
                        active: false,
                        route: '/app/policy/list/',
                    },
                    {
                        id: 'cancellation_policies',
                        title: 'Cancellation Policies',
                        active: false,
                        route: '/app/policy/cancellation-policy/list',
                    },
                    {
                        id: 'amenities_manager',
                        title: 'Amenities',
                        active: false,
                        route: '/app/policy/amenity-manager/list/',
                    },
                    {
                        id: 'meta_data_manager',
                        title: 'Amenity Metadata',
                        active: true,
                        route: '/app/policy/meta-data/list/',
                    },
                ],
            };
        },
        computed: {
            columnDefs() {
                return [
                    {
                        headerName: 'Name',
                        field: 'name',
                    },
                    {
                        headerName: 'Amenity',
                        field: 'amenity',
                    },
                    {
                        headerName: 'Instructions',
                        field: 'instructions',
                        minWidth: 160,
                    },
                    {
                        headerName: 'Metadata',
                        minWidth: 200,
                        field: 'metadata',
                        cellStyle: {
                            textTransform: 'capitalize',
                        },
                        autoWidthExclude: true,
                        cellRenderer: ({ data }) =>
                            data.metadata ? parseMetaDataToHumanReadString(data.metadata, data.amenity) : '',
                        autoHeight: true,
                    },
                    {
                        field: '',
                        cellRenderer: 'actionsCellRenderer',
                        cellClass: 'ag-right-aligned-cell',

                        cellRendererParams: {
                            updateAmenityMetadata: async (id, body) => {
                                try {
                                    await API.patch(AMENITIES_META_DATA_LIST_ENDPOINT(id), body);
                                    await this.fetchAmenitiesMetaData();
                                } catch (e) {
                                    this.errorToast({
                                        text: 'Could not save metadata',
                                    });
                                }
                            },
                            deleteAmenityMetadata: async (id) => {
                                try {
                                    await API.delete(AMENITIES_META_DATA_LIST_ENDPOINT(id));
                                    await this.fetchAmenitiesMetaData();
                                } catch (e) {
                                    this.errorToast({
                                        text: 'Could not save metadata',
                                    });
                                }
                            },
                        },
                    },
                ];
            },
            propertiesColumnDefs() {
                return [
                    {
                        field: 'id',
                        headerName: 'P. ID',
                    },
                    {
                        field: 'name',
                        headerName: 'Name',
                        autoWidthExclude: true,
                    },
                    {
                        field: '',
                        cellClass: 'ag-right-aligned-cell',
                        minWidth: 200,

                        cellRenderer: 'propertiesActionsCellRenderer',
                        cellRendererParams: {
                            assignedPropertiesFilter: this.assignedPropertiesFilter,
                            assignAmenity: async (id) => {
                                await this.assignMetaDataToProperty(id);
                                await this.fetchProperties();
                            },
                            removeAmenity: async (id) => {
                                try {
                                    await this.removeMetaDataFromProperty(id);
                                    await this.fetchProperties();
                                } catch (e) {
                                    this.errorToast({
                                        text: 'Could not delete amenity metadata',
                                    });
                                }
                            },
                        },
                    },
                ];
            },
            filterTitle() {
                return `"${this.selectedRowName}" is ${this.assignedPropertiesFilter ? 'assigned' : 'not assigned'}`;
            },
            filterIcon() {
                return !!this.assignedPropertiesFilter
                    ? `<img class="metadata-assign-icon" src="${AssignIcon}"></img>`
                    : `<img class="metadata-assign-icon" src="${UnAssignIcon}"></img>`;
            },
            propertiesFilterOptions() {
                return [
                    {
                        label: 'Assigned',
                        click: () => {
                            this.assignedPropertiesFilter = true;
                            this.fetchProperties();
                        },
                        customHTML: `<img class="metadata-assign-icon" src="${AssignIcon}"></img>`,
                        dropDownItemClass: 'filter-by-status-dropdown-item',
                    },
                    {
                        label: 'Not Assigned',
                        click: () => {
                            this.assignedPropertiesFilter = false;
                            this.fetchProperties();
                        },
                        customHTML: `<img class="metadata-assign-icon" src="${UnAssignIcon}"></img>`,
                        dropDownItemClass: 'filter-by-status-dropdown-item',
                    },
                ];
            },
            createMetaDataModalId() {
                return 'create-meta-data-modal';
            },
            selectedRowName() {
                return this.selectedAmenityRow?.name;
            },
        },
        mounted() {
            Promise.all([this.fetchAmenitiesMetaData()]).then(() => {
                this.initialRowSelect();
            });
        },
        methods: {
            getAmenityAdditionalMetaDataOptions,
            async assignMetaDataToProperty(property_id) {
                try {
                    this.busy = true;
                    await API.post(
                        `${AMENITIES_META_DATA_LIST_ENDPOINT(this.selectedAmenityRow.id)}assign-to-property/`,
                        {
                            property_id,
                        }
                    );
                } catch (e) {
                    this.errorToast({
                        text: 'Could not assign metadata to property',
                    });
                } finally {
                    this.busy = false;
                }
            },
            async fetchAmenitiesMetaData() {
                try {
                    this.busy = true;

                    const { data } = await API.get(AMENITIES_META_DATA_LIST_ENDPOINT());
                    this.rowData = data;
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch amenities' });
                } finally {
                    this.busy = false;
                }
            },
            async createAmenity(amenityObj, amenitiesList) {
                try {
                    this.busy = true;
                    const body = generateRequestBody(amenityObj, amenitiesList);
                    await API.post(AMENITIES_META_DATA_LIST_ENDPOINT(), body);
                    await this.fetchAmenitiesMetaData();
                    this.$bvModal.hide(this.createMetaDataModalId);
                } catch (e) {
                    const { generic, fields } = formatResponseError(e);

                    if (generic) {
                        this.errorToast({ text: generic });
                    } else {
                        this.errorToast({ text: 'Could not save amenity' });
                    }
                    if (!generic && !isEmpty(fields)) {
                        this.$refs.create_amenity_metadata.$children[0].setErrors(fields);
                    }
                } finally {
                    this.busy = false;
                }
            },
            async fetchProperties() {
                if (this.selectedAmenityRow) {
                    try {
                        this.busy = true;
                        const { data } = await API.get(
                            `${AMENITIES_META_DATA_LIST_ENDPOINT(this.selectedAmenityRow?.id)}properties/?is_assigned=${
                                this.assignedPropertiesFilter
                            }`
                        );
                        this.propertiesList = data;
                    } catch (e) {
                        this.errorToast({ text: 'Could not fetch property' });
                    } finally {
                        this.busy = false;
                    }
                }
            },
            initialRowSelect() {
                const gridApi = this.$refs.grid.gridOptions.api;
                gridApi.forEachNodeAfterFilter((node, i) => i === 0 && node.setSelected(true));
            },
            handleRowSelect() {
                const gridApi = this.$refs.grid.gridOptions.api;
                const selectedRows = gridApi.getSelectedRows();

                this.selectedAmenityRow = selectedRows.length ? selectedRows[0] : null;
                this.fetchProperties();
            },
            async removeMetaDataFromProperty(property_id) {
                try {
                    this.busy = true;
                    await API.post(
                        `${AMENITIES_META_DATA_LIST_ENDPOINT(this.selectedAmenityRow.id)}remove-from-property/`,
                        {
                            property_id,
                        }
                    );
                } catch (e) {
                    this.errorToast({
                        text: 'Could not remove amenity from property',
                    });
                } finally {
                    this.busy = false;
                }
            },
            async bulkRemoveMetaDataFromProperty() {
                try {
                    this.busy = true;
                    await API.post(
                        `${AMENITIES_META_DATA_LIST_ENDPOINT(this.selectedAmenityRow.id)}remove-from-all-properties/`
                    );
                    await this.fetchProperties();
                } catch (e) {
                    this.errorToast({
                        text: 'Could not remove amenity from property',
                    });
                } finally {
                    this.busy = false;
                }
            },
            async bulkAssignMetaDataToProperty() {
                try {
                    this.busy = true;
                    await API.post(
                        `${AMENITIES_META_DATA_LIST_ENDPOINT(this.selectedAmenityRow.id)}assign-to-all-properties/`
                    );
                    await this.fetchProperties();
                } catch (e) {
                    this.errorToast({
                        text: 'Could not remove amenity from property',
                    });
                } finally {
                    this.busy = false;
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    :deep(.metadata-grid) {
        display: inline;
        .ag-row-hover,
        .ag-row-selected {
            background: rgba(33, 150, 243, 0.1);
            cursor: pointer;
        }
    }
    .buttons-wrapper {
        display: inline;
        height: 100% !important;
    }
</style>

<style lang="scss">
    .metadata-assign-icon {
        width: 17.5px;
        height: 17.5px;
        margin-right: 5px;
    }
    .metadata-property-grid {
        .filter-by-status-dropdown-item {
            .dropdown-item {
                display: flex !important;
            }
        }
    }
</style>
